import { type TillKiosk } from "~/api/tills/types";

export type KioskPaymentMethod = "card" | "swish" | "credit" | "billing";

/**
 * Describes which payment button(s) the kiosk basket footer should render for
 * the current till and basket total. The component switches on `type` — it
 * never re-derives availability.
 *
 * - `none`   — no payment method is available.
 * - `single` — one "Order & pay" (or "Confirm order" for credit and billing)
 *   button that starts `method` directly. Covers a zero-total credit order, an
 *   invoiced order on a billing module, and tills that offer only card or only
 *   swish.
 * - `choice` — the user picks between card and swish, so each gets its own
 *   branded button.
 */
export type KioskPaymentButtons =
  | { type: "none" }
  | { type: "single"; method: KioskPaymentMethod }
  | { type: "choice"; methods: ("card" | "swish")[] };

type GetKioskPaymentButtonsArgs = {
  till: TillKiosk;
  /** Includes deposits, so a basket of free products can still owe money. */
  unitTotalPayableInclVat: number;
  isUserIdentified: boolean;
};

export function getKioskPaymentButtons({
  till,
  unitTotalPayableInclVat,
  isUserIdentified
}: GetKioskPaymentButtonsArgs): KioskPaymentButtons {
  if (unitTotalPayableInclVat === 0) {
    return { type: "single", method: "credit" };
  }

  // An unidentified order goes to the till's guest group or its own profile,
  // so an invoice would reach nobody: guests keep paying on the terminal.
  if (till.moduleConfig.paymentMethod === "billing" && isUserIdentified) {
    return { type: "single", method: "billing" };
  }

  const paymentProviders = till.paymentProviders;

  const hasCard = paymentProviders.includes("card");
  const hasSwish =
    paymentProviders.includes("swish") &&
    till.paymentGatewayConfig?.provider === "adyen";

  if (hasCard && hasSwish) {
    return { type: "choice", methods: ["card", "swish"] };
  }

  if (hasCard) {
    return { type: "single", method: "card" };
  }

  if (hasSwish) {
    return { type: "single", method: "swish" };
  }

  return { type: "none" };
}
